// src/pages/ServicesPage.js
import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Divider, Paper } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TextProvider, useText } from '../context/TextContext';

// Calculate years of experience helper function
const calculateYearsOfExperience = (experiences) => {
  if (!experiences || experiences.length === 0) return '0';
  
  try {
    // Find the first job experience (skip student entries)
    // Look for experiences with MM/YYYY format in duration
    let firstJobExperience = null;
    for (let i = experiences.length - 1; i >= 0; i--) {
      const exp = experiences[i];
      if (exp && exp.duration) {
        // Check if duration has MM/YYYY format (contains /)
        const startPart = exp.duration.split(/[–-]/)[0].trim();
        if (startPart.includes('/') && startPart.split('/').length === 2) {
          firstJobExperience = exp;
          break;
        }
      }
    }
    
    if (!firstJobExperience || !firstJobExperience.duration) {
      // Fallback: use the last experience
      firstJobExperience = experiences[experiences.length - 1];
    }
    
    if (!firstJobExperience || !firstJobExperience.duration) return '0';
    
    // Handle both en dash (–) and regular dash (-)
    const startDateStr = firstJobExperience.duration.split(/[–-]/)[0].trim();
    
    // Parse the date (format: MM/YYYY or YYYY)
    let month, year;
    if (startDateStr.includes('/')) {
      const dateParts = startDateStr.split('/');
      if (dateParts.length !== 2) return '0';
      month = parseInt(dateParts[0], 10);
      year = parseInt(dateParts[1], 10);
    } else {
      // If no slash, assume it's just a year
      year = parseInt(startDateStr, 10);
      month = 1; // Default to January
    }
    
    if (isNaN(year) || (month && isNaN(month))) return '0';
    
    const startDate = new Date(year, (month || 1) - 1, 1); // month is 0-indexed
    const now = new Date();
    
    // Calculate difference in years
    const yearsDiff = now.getFullYear() - startDate.getFullYear();
    const monthsDiff = now.getMonth() - startDate.getMonth();
    
    // If current month is before start month, subtract a year
    const totalYears = monthsDiff < 0 ? yearsDiff - 1 : yearsDiff;
    const totalMonths = monthsDiff < 0 ? 12 + monthsDiff : monthsDiff;
    
    // If we have at least 6 months into the current year, round up
    if (totalMonths >= 6) {
      return `${totalYears + 1}+`;
    }
    
    return `${totalYears}+`;
  } catch (error) {
    console.error('Error calculating years of experience:', error);
    return '0';
  }
};

const ServicesContent = () => {
  const textData = useText();
  const experiences = textData.experiences;
  const yearsOfExperience = calculateYearsOfExperience(experiences);
  const services = [
    {
      title: 'Software Development',
      description: 'Full-stack development services using modern technologies and best practices. I specialize in building scalable, maintainable applications using Microsoft .NET, React, and cloud technologies. From initial concept to deployment, I deliver solutions that meet both technical requirements and business objectives.',
      technologies: ['.NET', 'C#', 'React', 'TypeScript', 'Node.js', 'Python']
    },
    {
      title: 'Technical Leadership',
      description: 'Leading development teams and making technical decisions for scalable solutions. With experience leading a team of 12 in a large-scale scrum programme of 400+ people, I focus on establishing best practices, code quality standards, and fostering collaborative environments. I drive technical strategy, architecture decisions, and mentor team members to achieve excellence.',
      technologies: ['Team Leadership', 'Architecture', 'Code Reviews', 'Mentoring']
    },
    {
      title: 'Cloud Architecture',
      description: 'Designing and implementing cloud-based solutions using Microsoft Azure. I architect scalable, resilient systems leveraging Azure services including Service Bus, Event Hub, IoT Hub, Cosmos DB, and Azure Functions. My expertise includes microservices architecture, event-driven systems, and cloud-native application design.',
      technologies: ['Azure', 'Microservices', 'Event-Driven Architecture', 'Serverless']
    },
    {
      title: 'API Integration',
      description: 'Integrating third-party services including Stripe, Zoom, Calendly, and payment processors. I build robust integration layers that handle authentication, webhooks, error handling, and data synchronization. My experience includes working with REST APIs, GraphQL, OData, and real-time communication protocols.',
      technologies: ['Stripe', 'Zoom', 'Calendly', 'Payment Processors', 'Webhooks', 'OAuth']
    },
    {
      title: 'AI Agent Orchestration & AI-Powered Solutions',
      description: 'I actively orchestrate AI agents using Claude, Cursor, and related tooling to design, build, and ship software faster—while staying accountable for architecture and quality. I can work on any AI-powered project, including multi-agent workflows, LLM integrations, RAG/CAG pipelines, and intelligent product features that turn AI from a demo into production capability.',
      technologies: ['Claude', 'Cursor', 'AI Agent Orchestration', 'LLMs', 'RAG/CAG Pipelines', 'Qdrant', 'pgvector', 'Python']
    },
    {
      title: 'DevOps & Infrastructure',
      description: 'Setting up CI/CD pipelines, containerization, and infrastructure automation. I implement DevOps practices using GitHub Actions, Docker, Kubernetes, and infrastructure-as-code principles. My focus is on creating reliable, automated deployment processes that ensure code quality and system stability.',
      technologies: ['Docker', 'Kubernetes', 'GitHub Actions', 'CI/CD', 'Git']
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F3F2F1' }}>
      <Header />
      <Container sx={{ paddingY: { xs: 4, md: 6 }, flex: 1, maxWidth: '1200px' }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            letterSpacing: '-0.02em',
            marginBottom: 2,
            color: '#0078D4'
          }}
        >
          Services
        </Typography>
        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: '#4a4a4a',
            marginBottom: 5,
            fontWeight: 300,
            maxWidth: '800px'
          }}
        >
          Through Aldea Systems I offer cloud and data engineering for connected products as a B2B vendor
          partner—high-load platforms, event-driven pipelines, ETL and AI orchestration. With over {yearsOfExperience}{' '}
          years of industry experience, I combine technical expertise with practical problem-solving to deliver
          solutions that drive business value.
        </Typography>

          <Divider sx={{ marginY: 4, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ padding: 3 }}>
                    <Typography 
                      variant="h5" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 500,
                        marginBottom: 2,
                        color: '#1a1a1a'
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      paragraph 
                      sx={{ 
                        color: '#4a4a4a',
                        lineHeight: 1.7,
                        marginBottom: 3,
                        fontSize: '0.95rem'
                      }}
                    >
                      {service.description}
                    </Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {service.technologies.map((tech, techIndex) => (
                        <Paper
                          key={techIndex}
                          elevation={0}
                          sx={{
                            padding: '4px 12px',
                            backgroundColor: 'rgba(0, 120, 212, 0.06)',
                            borderRadius: '2px',
                            color: '#0078D4',
                            border: '1px solid rgba(0, 120, 212, 0.25)',
                            fontSize: '0.85rem',
                            fontWeight: 500,
                          }}
                        >
                          {tech}
                        </Paper>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
      </Container>
      <Footer />
    </Box>
  );
};

const ServicesPage = () => {
  return (
    <TextProvider>
      <ServicesContent />
    </TextProvider>
  );
};

export default ServicesPage;

